/**
 * Rig State - capture/recall primitive (CP-058, PLAN.md QUEUED "Rig States
 * (visual recall)"; renamed 2026-08-15 from "Snapshot," which collided
 * with DEC-151's Helix Snapshot item). A Rig State bank owns a fixed
 * target set (the UNION of its looks' captured targets); every look sets
 * ALL union targets - named to its own value, unnamed to the bank's HOME
 * default. Fires as an "instant chord" - the gap between FireButton
 * (1 action) and Timeline (sequenced over time).
 *
 * Milestone 2 (2026-08-15): this file is the data model only. Capture/
 * recall logic lives PWA-side in `rigState/store.ts` (schema stays free of
 * PWA-layer dependencies, same convention as `timeline.ts`'s `CueStep`).
 */

/** One captured or authored target value within a look. `value` is
 *  transport-agnostic - semitones for transpose/capo, a pitch class 0-11
 *  for autotuneKey, an `AutotuneScale` string for autotuneScale, 0-127 for
 *  a generic MIDI CC/note targetId. Interpretation is `liveState/
 *  resolve.ts`'s job at read/write time, not this shape's. */
export interface RigStateChordEntry {
  /** Opaque targetId - `midi:<port>:cc:<ch>:<cc>` / `midi:<port>:note:
   *  <ch>:<note>` for generic MIDI, or one of the `liveState/registry.ts`
   *  named ids (`'transpose'`, `'capo'`, `'autotuneKey'`, `'autotuneScale'`).
   *  Deliberately a plain string (CP-058's day-one stable targetId scheme),
   *  extensible to `osc:<addr>` / `dmx:<universe>:<ch>` / `obs:<source>:
   *  <field>` later without a re-keying migration. */
  targetId: string;
  value: number | string;
}

/**
 * A named, recallable state within a bank. Exactly one look per bank
 * should carry `isHome: true` - the "get-out" default every OTHER look's
 * gaps fall back to (locked semantics: "Radio-switching a bank is thus
 * always a complete crossfade; stranded lights impossible by
 * construction"). HOME is authored the same way as any other look, not a
 * special case in the data - just a flag.
 */
export interface RigStateLook {
  id: string;
  name: string;
  isHome?: boolean;
  values: RigStateChordEntry[];
  created: string;
  modified: string;
}

export interface RigStateBank {
  id: string;
  name: string;
  looks: RigStateLook[];
  created: string;
  modified: string;
}

export const RIG_STATE_SCHEMA_VERSION = 'opbuttons-rigstate/v1' as const;

export interface RigStateLibrary {
  schemaVersion: typeof RIG_STATE_SCHEMA_VERSION;
  banks: RigStateBank[];
}
